import React, { useState } from 'react';
import { History, MessageSquarePlus, RotateCcw, ShieldCheck } from 'lucide-react';
import Banner from '../../../common/Banner';
import ReasonDialog from '../../../common/ReasonDialog';
import { useToast } from '../../../common/Toast';
import { describeApiError } from '../../../../utils/apiErrors';
import {
  addChartAddendum,
  decideChartReopen,
  fetchChartVersions,
  requestChartReopen,
  ChartMeta,
  ChartVersionSnapshot
} from '../../../../services/patientmr/charting.service';
import { getEntryTitle } from '../utils';
import { convertEntryTypeFromApi } from '../api-utils';
import EntryContent from './EntryContent';
import { DownloadVisitNoteButton } from './VisitNoteActions';

type ChartStatus = 'DRAFT' | 'SUBMITTED' | 'QA' | 'COMPLETE';

export interface ChartIntegrityPanelProps {
  patientId: string;
  visitId: string;
  chartingStatus: ChartStatus;
  chart?: ChartMeta;
  currentUserId?: string;
  /** charting:update or updateall */
  canUpdateCharting: boolean;
  /** supervisor:approve (addendum) */
  canAddAddendum: boolean;
  /** supervisor:approve AND supervisor:reject (reopen decision) */
  canDecideReopen: boolean;
  /** Clinical manager or admin: may ask to reopen a chart they did not write */
  isManager: boolean;
  /** Reload entries and chart metadata after a change */
  onChanged: () => void | Promise<void>;
}

const formatAt = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const SIGNED_STATUSES: ChartStatus[] = ['SUBMITTED', 'QA', 'COMPLETE'];

/**
 * Phase 9C chart integrity: return-for-correction comment, admin-correction notices for the
 * author, the author's electronic signature, COMPLETE-chart reopen (request -> supervisor
 * decision), supervisor addenda and earlier read-only chart versions.
 */
const ChartIntegrityPanel: React.FC<ChartIntegrityPanelProps> = ({
  patientId, visitId, chartingStatus, chart, currentUserId,
  canUpdateCharting, canAddAddendum, canDecideReopen, isManager, onChanged
}) => {
  const { showToast } = useToast();
  const [dialog, setDialog] = useState<'request' | 'reject' | 'addendum' | null>(null);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState<ChartVersionSnapshot[] | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [openVersion, setOpenVersion] = useState<number | null>(null);

  if (!chart) return null;

  const isAuthor = Boolean(currentUserId && chart.providerId && String(chart.providerId) === String(currentUserId));
  const request = chart.reopenRequest;
  const pending = request?.status === 'PENDING';
  const isRequester = Boolean(pending && currentUserId && request?.requestedBy === currentUserId);
  const billed = chart.visitStatus === 'BILLED';
  const canRequestReopen = chartingStatus === 'COMPLETE' && !pending && !billed && canUpdateCharting && (isAuthor || isManager);
  const canDecide = pending && canDecideReopen && !isRequester;
  const canAddendum = canAddAddendum && !isAuthor;
  const notices = isAuthor ? chart.authorNotices || [] : [];

  const run = async (action: () => Promise<unknown>, success: string, fallback: string) => {
    try {
      setBusy(true);
      await action();
      setDialog(null);
      showToast(success, { variant: 'success' });
      await onChanged();
    } catch (error) {
      showToast(describeApiError(error, fallback), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const toggleVersions = async () => {
    const next = !showVersions;
    setShowVersions(next);
    if (next && versions === null) {
      try {
        setVersions(await fetchChartVersions(patientId, visitId));
      } catch (error) {
        setVersions([]);
        showToast(describeApiError(error, 'Failed to load earlier versions.'), { variant: 'error' });
      }
    }
  };

  return (
    <div className="space-y-2 mb-3" data-testid="chart-integrity-panel">
      {chartingStatus === 'DRAFT' && chart.returnComment && (
        <Banner variant="warning" title="Returned for correction" data-testid="chart-return-banner">
          {chart.returnComment.comment}
          <span className="block text-xs opacity-80 mt-0.5">{formatAt(chart.returnComment.at)}</span>
        </Banner>
      )}

      {notices.map((n, i) => (
        <Banner key={`${n.entryId}-${i}`} variant="info" title="An administrator corrected your chart" data-testid="chart-author-notice">
          A {getEntryTitle(convertEntryTypeFromApi(n.entryType || '')).toLowerCase()} entry was invalidated on {formatAt(n.at)}. The entry shows the reason.
        </Banner>
      ))}

      {pending && (
        <Banner
          variant="warning"
          title="Reopen requested"
          data-testid="chart-reopen-pending"
          action={canDecide ? (
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={() => run(() => decideChartReopen(patientId, visitId, true), 'Reopen approved. The chart is a draft again.', 'Failed to approve the reopen.')}
                className="rounded bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 min-h-[36px]">
                Approve
              </button>
              <button type="button" disabled={busy} onClick={() => setDialog('reject')}
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50 min-h-[36px]">
                Reject
              </button>
            </div>
          ) : undefined}
        >
          {request?.reason && <span className="block">{request.reason}</span>}
          <span className="block text-xs opacity-80 mt-0.5">
            {formatAt(request?.requestedAt)}
            {isRequester ? ' · waiting for a supervisor' : ''}
            {pending && canDecideReopen && isRequester ? ' · another supervisor must decide' : ''}
          </span>
        </Banner>
      )}

      {request?.status === 'REJECTED' && chartingStatus === 'COMPLETE' && (
        <Banner variant="neutral" title="Reopen request rejected" data-testid="chart-reopen-rejected">
          {request.decisionComment}
          <span className="block text-xs opacity-80 mt-0.5">{formatAt(request.decidedAt)}</span>
        </Banner>
      )}

      {SIGNED_STATUSES.includes(chartingStatus) && chart.attestation && (
        <div className="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900" data-testid="chart-attestation">
          <ShieldCheck size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
          <div>
            <div className="font-medium">
              Signed by {chart.attestation.name || 'the visit clinician'}{chart.attestation.credentials ? `, ${chart.attestation.credentials}` : ''} · {formatAt(chart.attestation.at)}
            </div>
            {chart.attestation.statement && <div className="text-xs opacity-80">{chart.attestation.statement}</div>}
          </div>
        </div>
      )}

      {/* Phase 18 M9: the internal visit note is always available; the rest when allowed */}
      <div className="flex flex-wrap gap-2">
          <DownloadVisitNoteButton patientId={patientId} visitId={visitId} />
          {canRequestReopen && (
            <button type="button" onClick={() => setDialog('request')} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 min-h-[44px] md:min-h-0">
              <RotateCcw size={14} aria-hidden="true" /> Request reopen
            </button>
          )}
          {canAddendum && (
            <button type="button" onClick={() => setDialog('addendum')} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm text-indigo-800 hover:bg-indigo-100 min-h-[44px] md:min-h-0">
              <MessageSquarePlus size={14} aria-hidden="true" /> Add addendum
            </button>
          )}
          {chart.version > 1 && (
            <button type="button" onClick={toggleVersions}
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 min-h-[44px] md:min-h-0">
              <History size={14} aria-hidden="true" /> {showVersions ? 'Hide earlier versions' : `Earlier versions (${chart.version - 1})`}
            </button>
          )}
      </div>

      {showVersions && (
        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="chart-versions">
          {versions === null ? (
            <div className="text-slate-500">Loading…</div>
          ) : versions.length === 0 ? (
            <div className="text-slate-500">No earlier versions.</div>
          ) : versions.map((v) => (
            <div key={v.version} className="border-b border-slate-200 last:border-0 py-1">
              <button type="button" className="font-medium text-slate-800 underline" onClick={() => setOpenVersion(openVersion === v.version ? null : v.version)}>
                Version {v.version}
              </button>
              <span className="ml-2 text-xs text-slate-500">
                {v.attestation?.name ? `signed by ${v.attestation.name} · ` : ''}completed {formatAt(v.completedAt)} · replaced {formatAt(v.supersededAt)}
              </span>
              {openVersion === v.version && (
                <div className="mt-1 space-y-1">
                  {v.entries.map((e) => {
                    const type = convertEntryTypeFromApi(e.entryType);
                    return (
                      <div key={e.entryId} className="rounded border border-slate-200 bg-white p-1.5">
                        <div className="text-xs font-medium text-slate-600">{getEntryTitle(type)} · {formatAt(e.timestamp)}</div>
                        <EntryContent entry={{ id: e.entryId, type, date: e.timestamp || '', provider: '', data: e.data, invalidated: e.status === 'INVALIDATED' }} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ReasonDialog
        open={dialog === 'request'}
        title="Request to reopen this chart"
        description="A clinical supervisor reviews the request. If approved, the chart returns to draft for amendments and must be signed again. The completed version is kept read-only."
        label="Reason for reopening"
        confirmLabel="Send request"
        busy={busy}
        onConfirm={(reason) => run(() => requestChartReopen(patientId, visitId, reason), 'Reopen requested.', 'Failed to request the reopen.')}
        onCancel={() => setDialog(null)}
      />
      <ReasonDialog
        open={dialog === 'reject'}
        title="Reject the reopen request"
        description="The chart stays complete. Your comment is shown to the requester."
        label="Comment"
        confirmLabel="Reject"
        destructive
        busy={busy}
        onConfirm={(comment) => run(() => decideChartReopen(patientId, visitId, false, comment), 'Reopen rejected.', 'Failed to reject the reopen.')}
        onCancel={() => setDialog(null)}
      />
      <ReasonDialog
        open={dialog === 'addendum'}
        title="Add a supervisor addendum"
        description="The addendum is added to the chart under your name. It never changes the clinician's entries."
        label="Addendum"
        confirmLabel="Add addendum"
        maxLength={4000}
        busy={busy}
        onConfirm={(note) => run(() => addChartAddendum(patientId, visitId, note), 'Addendum added.', 'Failed to add the addendum.')}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
};

export default ChartIntegrityPanel;
